import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./StaticPriceOverrides.styles.scss";

// Component Imports
import { Loading } from "@/layout";
import { AGTable, AlertMessage } from "@/components";
import DeleteModal from "@/components/Modals/DeleteModal/DeleteModal";
import { StaticPriceOverrideUploadModal } from "@/components/Modals/StaticPriceOverride";

// Package Imports
import { v4 as uuidv4 } from "uuid";

// MUI Imports
import { Button, SvgIcon } from "@mui/material";

// SVG Imports
import LogoSVG from "@/svgs/pulse_prime_logo.svg?react";

// Utils/Formatting Imports
import { useAlert } from "@/utils/hooks/useAlert/useAlert";
import {
  getStaticPriceOverrides,
  deleteStaticPriceOverride,
  getAllInstrumentSymbols,
  postStaticPriceOverride,
} from "@/utils/promises";
import {
  ColDef,
  GetContextMenuItemsParams,
  MenuItemDef,
} from "ag-grid-community";
import StaticPriceOverrideDetailsModal from "@/components/Modals/StaticPriceOverride/StaticPriceOverrideDetailsModal/StaticPriceOverrideDetailsModal";
import { IInstrumentDefinition } from "@pulse-ui/codegen/generated_ts/refdata";

// Type Imports
import { IAlertMessage } from "@pulse-ui/types/components/AlertMessage";
import {
  IAGStaticPriceOverrideDataRow,
  IIntervals,
  IIntervalsExtended,
  IStaticOverrideSchedule,
} from "@pulse-ui/types/components/AGTable";
import { ISelectOption } from "@pulse-ui/types/components/DropdownInput";
import {
  IStaticPriceOverrideDetailsModalStatus,
  IStaticPriceOverrideUploadModalStatus,
} from "./StaticPriceOverrides.types";

const StaticPriceOverrides = () => {
  // State - START -------------------------------------
  const location_origin = window.location.origin;

  // General States
  const gridRef = useRef();
  const [rowData, setRowData] = useState<IAGStaticPriceOverrideDataRow[]>([]);
  const [intervalsMap, setIntervalsMap] = useState<
    Map<string, IIntervalsExtended>
  >(new Map());
  const [loading, setLoading] = useState<boolean>(true);
  const [instrumentsOptions, setInstrumentsOptions] = useState<ISelectOption[]>(
    []
  );
  const [activeSymbols, setActiveSymbols] = useState<string[]>([]);

  // Error Handling / Alert Message States
  const [errors, setErrors] = useState({
    symbol: false,
    unique_symbol: false,
    start_time: false,
    end_time: false,
    day: false,
    price: false,
  });
  const { alerts, pushAlert, closeAlert, clearAlerts } = useAlert();

  // Modal States
  const [
    staticPriceOverrideDetailsModalStatus,
    setStaticPriceOverrideDetailsModalStatus,
  ] = useState<IStaticPriceOverrideDetailsModalStatus>({
    open: false,
    schedule: null,
  });
  const [deleteModalStatus, setDeleteModalStatus] = useState<{
    open: boolean;
    item_name: string | null;
  }>({
    open: false,
    item_name: null,
  });

  const [
    staticPriceOverrideUploadModalStatus,
    setStaticPriceOverrideUploadModalStatus,
  ] = useState<IStaticPriceOverrideUploadModalStatus>({
    open: false,
    id: null,
    symbol: null,
    start_time: null,
    end_time: null,
    day: null,
    price: null,
    type: null,
    all_day: false,
    originalRow: null,
  });
  // State - END ---------------------------------------

  // Methods - START -----------------------------------
  const parse_static_price_override_to_row_data = (
    static_price_overrides: IStaticOverrideSchedule[]
  ): IAGStaticPriceOverrideDataRow[] => {
    let active_symbols: string[] = [];

    if (!static_price_overrides || static_price_overrides.length === 0) {
      return [];
    }

    const static_price_overrides_map = static_price_overrides.map(
      (override: IStaticOverrideSchedule) => {
        const {
          symbol,
          monday,
          tuesday,
          wednesday,
          thursday,
          friday,
          saturday,
          sunday,
        } = override;

        active_symbols.push(symbol);

        return {
          row_id: symbol,
          symbol: symbol,
          monday:
            monday.length > 0
              ? monday
                  .map(
                    (item) =>
                      `[${item.start_time} - ${item.end_time} ($${parseFloat(
                        item.price
                      )})]`
                  )
                  .join(", ")
              : "-",
          tuesday:
            tuesday.length > 0
              ? tuesday
                  .map(
                    (item) =>
                      `[${item.start_time} - ${item.end_time} ($${parseFloat(
                        item.price
                      )})]`
                  )
                  .join(", ")
              : "-",
          wednesday:
            wednesday.length > 0
              ? wednesday
                  .map(
                    (item) =>
                      `[${item.start_time} - ${item.end_time} ($${parseFloat(
                        item.price
                      )})]`
                  )
                  .join(", ")
              : "-",
          thursday:
            thursday.length > 0
              ? thursday
                  .map(
                    (item) =>
                      `[${item.start_time} - ${item.end_time} ($${parseFloat(
                        item.price
                      )})]`
                  )
                  .join(", ")
              : "-",
          friday:
            friday.length > 0
              ? friday
                  .map(
                    (item) =>
                      `[${item.start_time} - ${item.end_time} ($${parseFloat(
                        item.price
                      )})]`
                  )
                  .join(", ")
              : "-",
          saturday:
            saturday.length > 0
              ? saturday
                  .map(
                    (item) =>
                      `[${item.start_time} - ${item.end_time} ($${parseFloat(
                        item.price
                      )})]`
                  )
                  .join(", ")
              : "-",
          sunday:
            sunday.length > 0
              ? sunday
                  .map(
                    (item) =>
                      `[${item.start_time} - ${item.end_time} ($${parseFloat(
                        item.price
                      )})]`
                  )
                  .join(", ")
              : "-",
          original_row: override,
        };
      }
    );

    setActiveSymbols(active_symbols);

    return static_price_overrides_map;
  };

  const resetUploadModalStatus = () => {
    setStaticPriceOverrideUploadModalStatus({
      open: false,
      id: null,
      symbol: null,
      start_time: null,
      end_time: null,
      day: null,
      price: null,
      type: null,
      all_day: false,
      originalRow: null,
    });
  };

  const fetchActiveStaticPriceOverrides = async (refetched_symbol?: string) => {
    try {
      if (!loading) setLoading(true);

      const get_static_price_overrides = await getStaticPriceOverrides(
        location_origin
      );

      if (
        get_static_price_overrides.data &&
        get_static_price_overrides.data.length > 0
      ) {
        const { data } = get_static_price_overrides;

        const row_data = parse_static_price_override_to_row_data(data);
        setRowData(row_data);

        if (refetched_symbol) {
          const symbol_schedule = data.find(
            (schedule: IStaticOverrideSchedule) =>
              schedule.symbol === refetched_symbol
          );

          setStaticPriceOverrideDetailsModalStatus({
            open: true,
            schedule: parseScheduleIntervalsWithUuids(symbol_schedule),
          });
        }
      } else {
        pushAlert({
          id: uuidv4(),
          message: "No Static Price Overrides found.",
          status: 408,
        });

        setStaticPriceOverrideDetailsModalStatus({
          open: false,
          schedule: null,
        });
      }
    } catch (error_res: any) {
      pushAlert({
        id: uuidv4(),
        message: error_res.error,
        status: error_res?.status || 500,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateSymbolStaticPriceOverride = async (symbol: string) => {
    try {
      setLoading(true);

      await deleteStaticPriceOverride(location_origin, symbol);

      pushAlert({
        id: uuidv4(),
        message: `Static Price Overrides for ${symbol} have been removed.`,
        status: 200,
      });
    } catch (error_res: any) {
      pushAlert({
        id: uuidv4(),
        message: error_res.error,
        status: error_res?.status || 500,
      });
    } finally {
      setDeleteModalStatus({
        open: false,
        item_name: null,
      });
      fetchActiveStaticPriceOverrides();
      setLoading(false);
    }
  };

  const validateForm = (
    symbol: string,
    unique_symbol: string,
    start_time: string,
    end_time: string,
    day: string,
    price: string
  ) => {
    const newErrors = {
      symbol: !symbol || symbol === "",
      unique_symbol:
        staticPriceOverrideUploadModalStatus.type === "add" &&
        activeSymbols.includes(unique_symbol),
      start_time:
        !start_time ||
        start_time === null ||
        (start_time !== "00:00:00" &&
          end_time !== "00:00:00" &&
          start_time >= end_time),
      end_time:
        !end_time ||
        end_time === null ||
        (start_time !== "00:00:00" &&
          end_time !== "00:00:00" &&
          end_time <= start_time),
      day: !day || day === "",
      price:
        !price ||
        price === "" ||
        price === "-1.00" ||
        price === "-1" ||
        isNaN(parseFloat(price)),
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((error) => !error);
  };

  const handlePostStaticPriceOverride = async () => {
    try {
      setLoading(true);

      const { symbol, start_time, end_time, day, price } =
        staticPriceOverrideUploadModalStatus;

      const symbol_str = symbol || "";
      const start_time_str = start_time?.format("HH:mm:ss") || "";
      const end_time_str = end_time?.format("HH:mm:ss") || "";
      const day_lowercase = day?.toLowerCase() || "";
      const parsed_price = parseFloat(price || "-1").toString();

      const isValid = validateForm(
        symbol_str,
        symbol_str,
        start_time_str,
        end_time_str,
        day_lowercase,
        parsed_price
      );

      if (isValid) {
        const payload: IStaticOverrideSchedule = {
          symbol: symbol_str,
          monday: [],
          tuesday: [],
          wednesday: [],
          thursday: [],
          friday: [],
          saturday: [],
          sunday: [],
        };

        const new_schedule: IIntervals = {
          start_time: start_time_str,
          end_time: end_time_str,
          price: parsed_price.toString(),
        };

        if (day_lowercase in payload) {
          (payload[
            day_lowercase as keyof IStaticOverrideSchedule
          ] as IIntervals[]) = [new_schedule];
        }

        await postStaticPriceOverride(location_origin, [payload]);
        fetchActiveStaticPriceOverrides(payload.symbol);
        resetUploadModalStatus();
      }
    } catch (error_res: any) {
      pushAlert({
        id: uuidv4(),
        message: error_res.error,
        status: error_res?.status || 500,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStaticPriceOverride = async () => {
    try {
      setLoading(true);

      const { id, symbol, start_time, end_time, day, price } =
        staticPriceOverrideUploadModalStatus;

      const symbol_str = symbol || "";
      const start_time_str = start_time?.format("HH:mm:ss") || "";
      const end_time_str = end_time?.format("HH:mm:ss") || "";
      const day_lowercase = day?.toLowerCase() || "";
      const parsed_price = parseFloat(price || "-1").toString();

      const isValid = validateForm(
        symbol_str,
        symbol_str,
        start_time_str,
        end_time_str,
        day_lowercase,
        parsed_price
      );

      if (isValid && id) {
        const temp_interval: IIntervalsExtended = {
          uuid: id,
          symbol: symbol_str,
          weekday: day_lowercase,
          start_time: start_time_str,
          end_time: end_time_str,
          price: parsed_price.toString(),
        };

        intervalsMap.set(id, temp_interval);

        const payload: IStaticOverrideSchedule = {
          symbol: symbol_str,
          sunday: [],
          monday: [],
          tuesday: [],
          wednesday: [],
          thursday: [],
          friday: [],
          saturday: [],
        };

        intervalsMap.forEach((interval: IIntervalsExtended) => {
          const { weekday, start_time, end_time, price } = interval;
          const new_schedule: IIntervals = {
            start_time,
            end_time,
            price,
          };

          if (
            Array.isArray(
              payload[weekday.toLowerCase() as keyof IStaticOverrideSchedule]
            )
          ) {
            (
              payload[
                weekday.toLowerCase() as keyof IStaticOverrideSchedule
              ] as IIntervals[]
            ).push(new_schedule);
          }
        });

        await deleteStaticPriceOverride(location_origin, symbol_str);
        await postStaticPriceOverride(location_origin, [payload]);

        setStaticPriceOverrideDetailsModalStatus((prev) => ({
          ...prev,
          schedules_map: intervalsMap,
        }));
        fetchActiveStaticPriceOverrides(payload.symbol);
        resetUploadModalStatus();
      }
    } catch (error_res: any) {
      pushAlert({
        id: uuidv4(),
        message: error_res.error,
        status: error_res?.status || 500,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveScheduleFromStaticPriceOverride = async () => {
    try {
      setLoading(true);

      const { id, symbol, day } = staticPriceOverrideUploadModalStatus;
      const symbol_str = symbol || "";

      if (!id || !day) {
        pushAlert({
          id: uuidv4(),
          message: "Missing required fields for deletion.",
          status: 400,
        });
        return;
      }

      intervalsMap.delete(id);

      await deleteStaticPriceOverride(location_origin, symbol_str);

      if (intervalsMap.size > 0) {
        const payload: IStaticOverrideSchedule = {
          symbol: symbol_str,
          sunday: [],
          monday: [],
          tuesday: [],
          wednesday: [],
          thursday: [],
          friday: [],
          saturday: [],
        };

        intervalsMap.forEach((interval: IIntervalsExtended) => {
          const { weekday, start_time, end_time, price } = interval;
          const new_schedule: IIntervals = {
            start_time,
            end_time,
            price,
          };

          if (
            Array.isArray(
              payload[weekday.toLowerCase() as keyof IStaticOverrideSchedule]
            )
          ) {
            (
              payload[
                weekday.toLowerCase() as keyof IStaticOverrideSchedule
              ] as IIntervals[]
            ).push(new_schedule);
          }
        });

        await postStaticPriceOverride(location_origin, [payload]);

        setStaticPriceOverrideDetailsModalStatus((prev) => ({
          ...prev,
          schedules_map: intervalsMap,
        }));
      } else {
        setStaticPriceOverrideDetailsModalStatus({
          open: false,
          schedule: null,
        });
      }

      fetchActiveStaticPriceOverrides(
        intervalsMap.size > 0 ? symbol_str : undefined
      );
      resetUploadModalStatus();
    } catch (error_res: any) {
      pushAlert({
        id: uuidv4(),
        message: error_res.error,
        status: error_res?.status || 500,
      });
    } finally {
      setLoading(false);
    }
  };

  const parseScheduleIntervalsWithUuids = (
    schedule: IStaticOverrideSchedule
  ): IStaticOverrideSchedule => {
    let schedule_with_uuids: IStaticOverrideSchedule = schedule;

    for (let key of Object.keys(
      schedule
    ) as (keyof IStaticOverrideSchedule)[]) {
      if (Array.isArray(schedule[key] as IIntervals[])) {
        (schedule_with_uuids[key] as IIntervalsExtended[]) = (
          schedule[key] as IIntervals[]
        ).map((interval: IIntervals) => {
          return {
            ...interval,
            uuid: uuidv4(),
            weekday: key.toUpperCase(),
            symbol: schedule.symbol,
          };
        });
      }
    }

    return schedule_with_uuids;
  };

  const handleInitialDataLoad = async () => {
    try {
      await fetchActiveStaticPriceOverrides();
      const symbols_res = await getAllInstrumentSymbols(location_origin);

      if (symbols_res.data) {
        const symbols = symbols_res.data.map(
          (symbol: IInstrumentDefinition) => {
            return {
              label: symbol.toString(),
              value: symbol.toString(),
            };
          }
        );
        setInstrumentsOptions(symbols);
      }
    } catch (error_res: any) {
      pushAlert({
        id: uuidv4(),
        message: error_res.error,
        status: error_res?.status || 500,
      });
    } finally {
      if (loading) setLoading(false);
    }
  };

  // Column Definitions: Defines & controls grid columns.
  const colDefs = useMemo<ColDef<IAGStaticPriceOverrideDataRow>[]>(
    () => [
      {
        headerName: "Symbol",
        field: "symbol",
        cellClass: "ag-center-aligned-cell",
      },
      {
        headerName: "Sunday",
        field: "sunday",
        cellClass: "ag-center-aligned-cell",
      },
      {
        headerName: "Monday",
        field: "monday",
        cellClass: "ag-center-aligned-cell",
      },
      {
        headerName: "Tuesday",
        field: "tuesday",
        cellClass: "ag-center-aligned-cell",
      },
      {
        headerName: "Wednesday",
        field: "wednesday",
        cellClass: "ag-center-aligned-cell",
      },
      {
        headerName: "Thursday",
        field: "thursday",
        cellClass: "ag-center-aligned-cell",
      },
      {
        headerName: "Friday",
        field: "friday",
        cellClass: "ag-center-aligned-cell",
      },
      {
        headerName: "Saturday",
        field: "saturday",
        cellClass: "ag-center-aligned-cell",
      },
    ],
    []
  );

  const defaultColDef: ColDef = {
    flex: 1,
  };

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): MenuItemDef[] => {
      const result: MenuItemDef[] = [
        {
          name: "View Details",
          action: () => {
            setStaticPriceOverrideDetailsModalStatus({
              open: true,
              schedule: parseScheduleIntervalsWithUuids(
                params.node?.data.original_row
              ),
            });
          },
          cssClasses: ["pulse-color--background-100"],
        },
        {
          name: "Deactivate Static Price Override",
          action: () => {
            setDeleteModalStatus({
              open: true,
              item_name: params.node?.data.symbol,
            });
          },
          cssClasses: ["pulse-color--error-400"],
        },
      ];
      return result;
    },
    [window]
  );
  // Methods - END -------------------------------------

  // useEffects - START --------------------------------
  useEffect(() => {
    handleInitialDataLoad();

    return () => {
      clearAlerts();
    };
  }, []);
  // useEffects - END ----------------------------------

  return (
    <div className="pulse-sidenav-adjusted-container static-price-overrides-container pulse-bg-color--background-900">
      <SvgIcon
        sx={{
          width: "100%",
          height: "100%",
          position: "absolute",
          top: 0,
          left: 0,
        }}
        children={<LogoSVG className="pulse-color--background-800" />}
      />

      {alerts.map((alert: IAlertMessage) => {
        return (
          <div className="pulse-mb-1" style={{ width: "100%" }} key={uuidv4()}>
            <AlertMessage
              id={alert.id}
              message={alert.message}
              status={alert.status}
              closeAlertMessage={closeAlert}
            />
          </div>
        );
      })}

      <div className="pulse-row static-price-overrides-header-row static-price-overrides-position-relative pulse-mb-2">
        <p className="pulse-typography-headline-6 pulse-color--background-50 pulse-col-md-4 pulse-col-lg-2 pulse-col-xl-2">
          Static Price Overrides
        </p>

        <Button
          variant="contained"
          onClick={() =>
            setStaticPriceOverrideUploadModalStatus({
              open: true,
              id: null,
              symbol: null,
              start_time: null,
              end_time: null,
              day: null,
              price: null,
              type: "add",
              all_day: false,
              originalRow: null,
            })
          }
          sx={{
            backgroundColor: "success.700",
            color: "success.100",
            typography: "fontSizeSmall",
            "&:hover": {
              backgroundColor: "success.600",
            },
            "&:disabled": {
              backgroundColor: "success.800",
              color: "success.300",
            },
          }}
        >
          Add Static Price Override
        </Button>
      </div>

      <AGTable
        gridRef={gridRef}
        rowData={rowData}
        colDefs={colDefs}
        defaultColDef={defaultColDef}
        groupDisplayType="singleColumn"
        sideBar={false}
        animateRows
        getContextMenuItems={getContextMenuItems}
      />

      {staticPriceOverrideDetailsModalStatus.open && (
        <StaticPriceOverrideDetailsModal
          open
          schedule={staticPriceOverrideDetailsModalStatus.schedule}
          handleClose={() => {
            setStaticPriceOverrideDetailsModalStatus({
              open: false,
              schedule: null,
            });
            setIntervalsMap(new Map());
          }}
          setStaticPriceOverrideUploadModalStatus={
            setStaticPriceOverrideUploadModalStatus
          }
          setIntervalsMap={setIntervalsMap}
        />
      )}

      {staticPriceOverrideUploadModalStatus.open && (
        <StaticPriceOverrideUploadModal
          staticPriceOverrideUploadModalStatus={
            staticPriceOverrideUploadModalStatus
          }
          errors={errors}
          instrumentsOptions={instrumentsOptions}
          setStaticPriceOverrideUploadModalStatus={
            setStaticPriceOverrideUploadModalStatus
          }
          handleClose={() => {
            setStaticPriceOverrideUploadModalStatus((prev) => ({
              ...prev,
              open: false,
            }));
            setErrors({
              symbol: false,
              unique_symbol: false,
              start_time: false,
              end_time: false,
              day: false,
              price: false,
            });
          }}
          handlePostStaticPriceOverride={handlePostStaticPriceOverride}
          handleUpdateStaticPriceOverride={handleUpdateStaticPriceOverride}
          handleRemoveScheduleFromStaticPriceOverride={
            handleRemoveScheduleFromStaticPriceOverride
          }
        />
      )}

      {deleteModalStatus.open && (
        <DeleteModal
          open
          item_name={deleteModalStatus.item_name}
          handleClose={() =>
            setDeleteModalStatus({
              open: false,
              item_name: null,
            })
          }
          handleSubmit={() =>
            handleDeactivateSymbolStaticPriceOverride(
              deleteModalStatus.item_name || ""
            )
          }
        />
      )}

      {loading && <Loading />}
    </div>
  );
};

export default StaticPriceOverrides;
